/**
 * Shared camera behaviour for the two capture screens — the driver's
 * at-scene camera and the dispatcher's PhotoShop yard camera.
 *
 * Both grab frames from a `getUserMedia` preview rather than handing off to
 * the phone's camera app, so neither one leaves copies in the photo library
 * for the operator to delete later.
 */

const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.85;

/**
 * Where the thumb is.
 *
 * Held upright, the bottom-centre is already under the thumb. Turned
 * sideways it isn't — the hand is on the phone's bottom edge, which is now
 * one of the side edges, so the shutter follows it there and sits halfway up.
 *
 * Side comes from the legacy `window.orientation` where it exists, because
 * iOS Safari reports `screen.orientation.angle` as the reverse of Android
 * (WebKit #254863) and would otherwise put the button on the far side from
 * the hand on iPhones. Both spellings agree on meaning: +90 leaves the
 * device's bottom edge on the right, -90 / 270 on the left.
 *
 * Landscape itself comes from a media query, never the angle — angle alone
 * can't tell you the phone is sideways, which is the trap an earlier
 * rotation fix fell into.
 */
export const readOrientation = () => {
  const landscape = typeof window !== "undefined"
    && window.matchMedia?.("(orientation: landscape)").matches;
  const angle = typeof window.orientation === "number"
    ? window.orientation
    : (window.screen?.orientation?.angle ?? 0);
  return {
    landscape: Boolean(landscape),
    side: angle === -90 || angle === 270 ? "left" : "right",
  };
};

/**
 * Subscribe to rotation. Three listeners, all of them needed: a 90-to-270
 * flip changes no dimensions at all, so only `orientationchange` fires for
 * that one, while `resize` catches desktop window drags.
 */
export const watchOrientation = (onChange) => {
  const mq = window.matchMedia?.("(orientation: landscape)");
  mq?.addEventListener?.("change", onChange);
  window.addEventListener("orientationchange", onChange);
  window.addEventListener("resize", onChange);
  return () => {
    mq?.removeEventListener?.("change", onChange);
    window.removeEventListener("orientationchange", onChange);
    window.removeEventListener("resize", onChange);
  };
};

/**
 * The frame the operator is actually looking at.
 *
 * The video is `object-cover`, so a landscape sensor feed held upright shows
 * a cropped centre strip — they frame a vehicle in that strip, and saving the
 * whole wide frame instead is what came back as "the photo is sideways": a
 * portrait shot that arrived landscape. Crop the source to the viewfinder's
 * own aspect ratio and save precisely what they saw.
 *
 * Deliberately NOT rotating anything, after THREE failed attempts (Nov 2026).
 * Two attempts to "correct" it from pixel dimensions both made things worse:
 *   1. Rotating on `screen.orientation.angle` — angle 0 means the device's
 *      NATURAL orientation, which is landscape on plenty of tablets, so this
 *      turned every photo on its side on the phones that were already fine.
 *   2. Rotating when the frame's aspect disagreed with the viewfinder's.
 *      That disagreement is the ORDINARY state of a landscape feed inside a
 *      tall preview — exactly what the crop above exists to handle — so it
 *      fired on normal portrait shots and rotated photos that were upright.
 *      Reported as "mostly coming out 90 degrees out, sideways".
 *
 * The lesson every time: pixel dimensions cannot tell you whether a frame
 * needs rotating. A wide frame in a tall window and a genuinely sideways
 * frame are indistinguishable to any geometry test.
 *
 *   3. (3 Nov 2026) Rotating by the device's own `VideoFrame.rotation`.
 *      Sounder than the first two — it is a fact, not a deduction — but it
 *      was applied to a camera whose REAL fault was a 27 Oct bump to
 *      2560x1440, a Samsung stream mode that hands back un-rotated frames.
 *      The dispatcher's camera never left 1080p and has never once produced
 *      a sideways photo on the same phones, which is the proof that these
 *      devices need no correction at all. Reverting the resolution is the
 *      fix; the rotation was an extra variable in an already murky bug, and
 *      drivers reported photos sideways "whenever they rotate the phone in
 *      their hand" while it was live. Removed.
 *
 * So: nothing here rotates anything. The device's rotation IS recorded with
 * each driver capture (see `readTrackRotation`, diagnostic only) so the next
 * bad photo can be explained from evidence, and the per-photo rotate control
 * in the gallery remains the escape hatch for one-off oddities.
 */
const framedCrop = (vw, vh, box) => {
  if (!box?.width || !box?.height) return { sx: 0, sy: 0, sw: vw, sh: vh };
  const scale = Math.max(box.width / vw, box.height / vh);   // object-cover
  const sw = Math.min(vw, box.width / scale);
  const sh = Math.min(vh, box.height / scale);
  return { sx: (vw - sw) / 2, sy: (vh - sh) / 2, sw, sh };
};

/**
 * DIAGNOSTIC ONLY — never applied to a pixel.
 *
 * Reports what the device says about how its frames are turned
 * (`VideoFrame.rotation`, 0/90/180/270). Recorded alongside each driver
 * capture so a sideways photo can be explained from evidence instead of
 * another theory. It was briefly USED to rotate captures on 3 Nov 2026 and
 * that was a mistake: the dispatcher's camera runs this same helper-free
 * path at 1080p and is flawless on the very same Samsung phones, so these
 * devices need no correction at all. Applying one only added a second
 * variable to a bug we were already struggling to pin down.
 *
 * Probes a `track.clone()` which is then stopped, so the live preview's own
 * sink is never touched — attaching a second reader to the rendered track
 * can stall it on some Chrome builds. 600ms race-timeout; anything missing
 * or thrown reports 0. Safari has no MediaStreamTrackProcessor.
 *
 * DO NOT wire this into captureFrame without hard evidence from a real
 * device that its frames actually need turning.
 */
export const readTrackRotation = async (track, { timeoutMs = 600 } = {}) => {
  const Processor = typeof window !== "undefined" && window.MediaStreamTrackProcessor;
  if (!Processor || !track?.clone) return 0;
  let probe = null;
  let reader = null;
  try {
    probe = track.clone();
    reader = new Processor({ track: probe }).readable.getReader();
    const first = await Promise.race([
      reader.read(),
      new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    const frame = first?.value;
    const rotation = Number(frame?.rotation) || 0;
    frame?.close?.();
    return [90, 180, 270].includes(rotation) ? rotation : 0;
  } catch {
    return 0;
  } finally {
    try { await reader?.cancel(); } catch { /* already closed */ }
    probe?.stop();
  }
};

/**
 * How the phone is PHYSICALLY held, from gravity — not from the screen.
 *
 * 4 Nov 2026, the actual root cause, proven with photographs. On live job
 * "TEST JOB 1 for Karen" four driver captures were recorded: two came out
 * upright, two came out 90 degrees over — and ALL FOUR reported identical
 * readings (frame 1080x1920, orientation portrait-primary, angle 0,
 * device_rotation 0). The driver had turned the phone sideways for the last
 * two, but Android's auto-rotate never fired, so the browser kept handing
 * back an upright portrait frame while the phone was held landscape. Every
 * API the app can read insisted nothing was wrong. That is why three
 * previous fixes missed it.
 *
 * Auto-rotate IS enabled on that phone (it reported angle 90 correctly on
 * another job the same day). It simply does not trigger when the phone is
 * tilted downward, because gravity cannot resolve which way is "up" — and
 * tilting down is exactly how you photograph a vehicle or a damaged panel.
 *
 * The accelerometer still knows. This is what the native camera app uses to
 * set a photo's orientation, and it is the only signal that survives the
 * screen staying put. Returns how far the capture must be turned CLOCKWISE
 * to stand upright: the phone's physical rotation MINUS the rotation the
 * screen already applied. When auto-rotate works normally those two agree
 * and the answer is 0 — so photos that are correct today stay untouched.
 */
export const requestMotionAccess = async () => {
  const Motion = typeof window !== "undefined" && window.DeviceMotionEvent;
  // iOS 13+ demands an explicit grant, from inside a user gesture. Opening
  // the camera is one. Android needs no permission.
  if (Motion?.requestPermission) {
    try { return (await Motion.requestPermission()) === "granted"; } catch { return false; }
  }
  return Boolean(Motion);
};

export const watchPhysicalRotation = (onChange) => {
  if (typeof window === "undefined" || !window.DeviceMotionEvent) return () => {};
  const GRAVITY_TRUST = 6;      // m/s^2 in-plane, ~37 deg of tilt. Was 4 (~24 deg),
                                // which let noise at 2 deg of tilt pose as truth.
  const STEADY_MS = 250;        // gravity must agree with itself before we believe it
  const RATE_DEADZONE = 2;      // deg/s below this is sensor bias, not a turn
  let anchor = 0;               // last quarter gravity genuinely agreed on
  let spin = 0;                 // degrees turned since that anchor, from the gyro
  let lastTs = 0;
  let steadyQuarter = null;
  let steadySince = 0;
  let last = null;

  const handle = (event) => {
    const g = event.accelerationIncludingGravity;
    const rate = event.rotationRate;
    if (!g || g.x == null || g.y == null) return;

    const now = event.timeStamp || performance.now();
    const dt = lastTs ? Math.min((now - lastTs) / 1000, 0.25) : 0;
    lastTs = now;

    // Integrate ALWAYS, not just while flat. The previous build only counted
    // spin once gravity had already gone blind, so any wobble past the old
    // threshold reset it mid-turn — a real 90 degree flat turn measured 17.
    if (dt && rate?.alpha != null && Math.abs(rate.alpha) > RATE_DEADZONE) {
      spin += rate.alpha * dt;
    }

    const inPlane = Math.hypot(g.x, g.y);
    if (inPlane >= GRAVITY_TRUST) {
      // Screen axes: +x right, +y up. Upright portrait reads (0, +9.8);
      // turned 90 clockwise reads (-9.8, 0). So clockwise = atan2(-x, y).
      const quarter = QUARTER((Math.atan2(-g.x, g.y) * 180) / Math.PI);
      if (quarter === steadyQuarter) {
        if (now - steadySince >= STEADY_MS) { anchor = quarter; spin = 0; }
      } else {
        steadyQuarter = quarter;
        steadySince = now;
      }
    } else {
      steadyQuarter = null;   // flat: gravity cannot see an in-plane turn at all
    }

    // Gravity where it can be trusted, gyro to carry across the flat patch.
    const physical = QUARTER(anchor + spin);
    // ADD the screen angle, never subtract it. Measured on the failing phone:
    // one physical position read gravity 270 and screen 90 — the screen
    // reports how far the CONTENT was counter-rotated, the inverse of the
    // phone's turn. 270 + 90 = 0, i.e. the screen already handled it.
    const screenAngle = Number(window.screen?.orientation?.angle ?? window.orientation ?? 0) || 0;
    const delta = QUARTER(physical + screenAngle);
    if (delta !== last) { last = delta; onChange(delta); }
  };

  window.addEventListener("devicemotion", handle);
  return () => window.removeEventListener("devicemotion", handle);
};

/**
 * Turn a sideways frame upright before anything else looks at it.
 *
 * Only ever called with a rotation derived from gravity (above), never from
 * pixel geometry. Verified in Chromium: a 1200x900 source with a top-left
 * marker rotates to 900x1200 with that marker at top-right.
 */
const uprightSource = (video, rotation) => {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const swap = rotation === 90 || rotation === 270;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? vh : vw;
  canvas.height = swap ? vw : vh;
  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);   // clockwise
  ctx.drawImage(video, -vw / 2, -vh / 2);
  return canvas;
};

/**
 * Grab the current frame as a JPEG, cropped to the viewfinder and capped at
 * 1600px on its longest edge.
 *
 * The cap matters: a raw full-sensor frame is a 2-4MB upload per shot off a
 * phone in a yard, against roughly 200KB here, and the backend shrinks it to
 * this size anyway. Same 1600 / q0.85 as `imageResize.js`, so a photo taken
 * here and one picked from a file are indistinguishable to an insurer.
 */
export const captureFrame = async (video, { maxEdge = MAX_EDGE_PX, quality = JPEG_QUALITY, rotation = 0 } = {}) => {
  if (!video?.videoWidth) return null;
  const rot = [90, 180, 270].includes(rotation) ? rotation : 0;
  const source = rot ? uprightSource(video, rot) : video;
  const srcW = rot ? source.width : video.videoWidth;
  const srcH = rot ? source.height : video.videoHeight;
  const box = video.getBoundingClientRect();
  // A phone held sideways frames a LANDSCAPE shot even though the screen
  // stayed portrait, so the crop window turns with it. Without this the
  // straightened frame would be sliced back down to a portrait strip and
  // throw away most of what the driver actually framed.
  const window_ = (rot === 90 || rot === 270) ? { width: box.height, height: box.width } : box;
  const { sx, sy, sw, sh } = framedCrop(srcW, srcH, window_);
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const w = Math.round(sw * scale);
  const h = Math.round(sh * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(source, sx, sy, sw, sh, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
  });
};
